import React, { useEffect } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/app/providers/AuthProvider';
import { TextInput } from '@/common/components/Inputs';
import { Notification } from '@/common/utils';
import { useUpdateCurrentUserMutation } from '@/entities/user';

const AccountSettingsModule = () => {
  const { t } = useTranslation();
  const { user, refreshUser } = useAuth();
  const updateCurrentUserMutation = useUpdateCurrentUserMutation();

  const methods = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: yupResolver(
      yup.object({
        firstName: yup.string().trim().required(t('Enter first name')),
        lastName: yup.string().trim().required(t('Enter last name')),
        email: yup
          .string()
          .email(t('Must be valid email address'))
          .required(t('Enter email')),
      })
    ),
    defaultValues: {
      email: user?.email || '',
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
    },
  });

  const { handleSubmit, reset } = methods;
  const { isDirty, isValid } = methods.formState;

  useEffect(() => {
    reset({
      email: user?.email || '',
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
    });
  }, [reset, user?.email, user?.firstName, user?.lastName]);

  const onSubmit = handleSubmit(async ({ firstName, lastName }) => {
    try {
      const trimmedFirstName = firstName.trim();
      const trimmedLastName = lastName.trim();

      await updateCurrentUserMutation.mutateAsync({
        firstName: trimmedFirstName,
        lastName: trimmedLastName,
      });
      await refreshUser();
      reset(
        {
          firstName: trimmedFirstName,
          lastName: trimmedLastName,
        },
        { keepDirty: false }
      );
      Notification.success(t('Account settings updated'));
    } catch (error) {
      Notification.error(
        error?.message || t('Failed to update account settings')
      );
    }
  });

  return (
    <FormProvider {...methods}>
      <Box
        component="form"
        onSubmit={onSubmit}
        sx={{
          padding: '24px',
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          height: '100%',
        }}
      >
        <Typography variant="h4" sx={{ mb: 6 }}>
          {t('Account settings')}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            p: 6,
            gap: 6,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: '12px',
            backgroundColor: 'white',
          }}
        >
          <Box width="100%" display="flex" gap={4} alignItems="center">
            <TextInput
              name="firstName"
              label={t('Your first name')}
              autoComplete="given-name"
              required
              containterSx={{ width: '100%', maxWidth: '440px' }}
            />
            <TextInput
              name="lastName"
              label={t('Your last name')}
              autoComplete="family-name"
              required
              containterSx={{ width: '100%', maxWidth: '440px' }}
            />
          </Box>
          <TextInput
            name="email"
            label={t('Email')}
            disabled
            required
            containterSx={{ width: '100%', maxWidth: '440px' }}
          />
        </Box>
        <Button
          type="submit"
          variant="contained"
          disabled={!isDirty || !isValid}
          loading={updateCurrentUserMutation.isPending}
          size="medium"
          sx={{
            width: 'fit-content',
            mt: 'auto',
            ml: 'auto',
            px: 4,
          }}
        >
          {t('Save updates')}
        </Button>
      </Box>
    </FormProvider>
  );
};

export default AccountSettingsModule;
